import React, { useState, useEffect } from 'react';
import { Megaphone, Trash2, Plus } from 'lucide-react';
import { campaignsAPI } from '../services/api';
import Modal from '../components/Modal';

const Campaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newCampaign, setNewCampaign] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const response = await campaignsAPI.getAll();
      setCampaigns(response.data || []);
    } catch (error) {
      console.error('Error loading campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this campaign?')) {
      try {
        await campaignsAPI.delete(id);
        loadCampaigns();
      } catch (error) {
        console.error('Error deleting campaign:', error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await campaignsAPI.create({
        ...newCampaign,
        status: 'Active'
      });
      setIsModalOpen(false);
      setNewCampaign({});
      loadCampaigns();
    } catch (error) {
      console.error('Error creating campaign:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      <div className="flex justify-end">
        <button 
          onClick={() => setIsModalOpen(true)}
          className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 shadow-sm"
          data-testid="new-campaign-btn"
        >
          <Plus size={20} className="mr-2" />
          New Campaign
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {campaigns.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500 bg-white rounded-xl border border-gray-200">
             No campaigns running.
          </div>
        ) : (
          campaigns.map(camp => (
            <div key={camp.id} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 group relative">
              <button 
                onClick={() => handleDelete(camp.id)}
                className="absolute top-4 right-4 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`delete-campaign-${camp.id}`}
              >
                <Trash2 size={18} />
              </button>
              <div className="flex items-center space-x-3 mb-4">
                <div className="bg-orange-100 text-orange-600 p-3 rounded-full">
                  <Megaphone size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">{camp.name}</h3>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded bg-green-100 text-green-700">
                    {camp.status}
                  </span>
                </div>
              </div>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Type:</span>
                  <span className="font-medium">{camp.type || 'Email'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Budget:</span>
                  <span className="font-medium">${camp.budget || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>Start Date:</span>
                  <span className="font-medium">{camp.start_date || 'Not set'}</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Create Campaign">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name</label>
            <input 
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-teal-500"
              value={newCampaign.name || ''}
              onChange={e => setNewCampaign({...newCampaign, name: e.target.value})}
              data-testid="campaign-name-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
             <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                value={newCampaign.type || 'Email'}
                onChange={e => setNewCampaign({...newCampaign, type: e.target.value})}
              >
                <option value="Email">Email</option>
                <option value="SMS">SMS</option>
                <option value="Social">Social Media</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Budget ($)</label>
              <input 
                type="number"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-teal-500"
                value={newCampaign.budget || ''}
                onChange={e => setNewCampaign({...newCampaign, budget: Number(e.target.value)})}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input 
              type="date"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-teal-500"
              value={newCampaign.start_date || ''}
              onChange={e => setNewCampaign({...newCampaign, start_date: e.target.value})}
            />
          </div>
          <div className="pt-4 flex justify-end space-x-3">
             <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
             <button type="submit" className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700" data-testid="launch-campaign-btn">Launch</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Campaigns;
